import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import studentService from "../../services/studentService";
import courseService from "../../services/courseService";
import ConfirmModal from "../../components/common/ConfirmModal";
import StudentScholarships from "../../components/StudentScholarships";
import {
    FiCreditCard,
    FiCheckSquare
} from "react-icons/fi";

// ── Helpers ───────────────────────────────────────────────────────────────────
const inr = (v) => (v != null && v !== "" && !isNaN(v)) ? `₹ ${Number(v).toLocaleString("en-IN")}` : "—";
const val = (v) => (v != null && v !== "" && v !== "–") ? v : <span style={{ color: "#d1d5db" }}>—</span>;
const bool = (v) => v === true ? "Yes" : v === false ? "No" : <span style={{ color: "#d1d5db" }}>—</span>;

const STATUS_COLORS = {
    Active: "#003b67",
    Passed: "#16a34a",
    Detained: "#d97706",
    Dropped: "#6b7280",
    "Passed Out": "#6b7280",
};

// ── Row component ─────────────────────────────────────────────────────────────
const Row = ({ label, value, wide }) => (
    <div style={{
        display: "flex",
        padding: "0.5rem 0",
        borderBottom: "1px solid var(--border)",
        gap: "1rem",
        alignItems: "flex-start",
        gridColumn: wide ? "1 / -1" : undefined,
    }}>
        <span style={{ minWidth: "200px", fontSize: 12.5, color: "var(--text-muted)", fontWeight: 500, flexShrink: 0 }}>
            {label}
        </span>
        <span style={{ fontSize: 14, color: "var(--text)", wordBreak: "break-word" }}>
            {value}
        </span>
    </div>
);

// ── Section wrapper ───────────────────────────────────────────────────────────
const Section = ({ title, accent = "#003b67", children, cols = 2 }) => (
    <div className="card" style={{ marginBottom: "1rem" }}>
        <div className="card-header" style={{ borderLeft: `4px solid ${accent}`, paddingLeft: "0.9rem" }}>
            {title}
        </div>
        <div
            className="card-body"
            style={{
                display: cols > 1 ? "grid" : "block",
                gridTemplateColumns: cols > 1 ? "repeat(auto-fill, minmax(320px, 1fr))" : undefined,
                gap: "0 2rem",
                padding: "0.25rem 1.25rem 0.75rem",
            }}
        >
            {children}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────

export default function StudentView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { role, isClerk } = useApp();
    const isAccountant = role === "accountant";
    const basePath = isClerk ? "/clerk/students" : "/students";
    const feeStatementPath = isClerk
        ? `/clerk/fees/student/${id}/statement`
        : isAccountant
        ? `/accountant/fees/student/${id}/statement`
        : `/fees/student/${id}/statement`;

    const [student, setStudent] = useState(null);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        courseService
            .getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch(() => {});
    }, []);

    const getCourseName = useCallback((courseVal) => {
        if (!courseVal) return "";
        if (typeof courseVal === "object") {
            return courseVal.shortName || courseVal.courseName || "";
        }
        const match = courses.find((c) => c._id === courseVal || c.shortName === courseVal || c.courseName === courseVal);
        if (match) return match.shortName || match.courseName;
        return courseVal || "";
    }, [courses]);

    useEffect(() => {
        (async () => {
            try {
                const res = await studentService.getById(id);
                setStudent(res.data);
            } catch (err) {
                toast.error(err.message);
                navigate(basePath);
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate, basePath]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await studentService.remove(id);
            toast.success(`"${student.fullName}" deleted`);
            navigate(basePath);
        } catch (err) {
            toast.error(err.message);
            setDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading student...</div>;
    if (!student) return null;

    const s = student;
    const sc = STATUS_COLORS[s.status] || "#003b67";
    const studentPhoto = s.studentPhoto?.url;
    const initials = s.fullName
        ?.split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "ST";

    return (
        <div style={{ maxWidth: 950 }}>

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <h1 className="page-title" style={{ margin: 0 }}>{s.fullName}</h1>
                        <span style={{ background: sc + "22", color: sc, padding: "2px 10px", borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                            {s.status}
                        </span>
                    </div>
                    <p className="page-subtitle" style={{ marginTop: 4 }}>
                        {s.enrollmentNo}
                        {getCourseName(s.course) && ` · ${getCourseName(s.course)} ${s.yearNumber ? `${s.yearNumber} Year` : ""}`}
                        {s.academicYear?.label && ` · ${s.academicYear.label}`}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    <button className="btn btn-outline" onClick={() => navigate(basePath)}>← Back</button>
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            const cId = s.course?._id || s.course || "";
                            const yNum = s.yearNumber || "";
                            navigate(`/attendance/report?studentId=${id}&course=${cId}&yearNumber=${yNum}`);
                        }}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.45rem",
                            fontWeight: 600,
                        }}
                    >
                        <FiCheckSquare size={15} /> View Attendance
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate(feeStatementPath)}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.45rem",
                            fontWeight: 600,
                        }}
                    >
                        <FiCreditCard size={15} /> View Fee Statement
                    </button>
                    <button className="btn btn-outline" onClick={() => navigate(`${basePath}/${id}/id-card`)}>🪪 ID Card</button>
                    <button className="btn btn-outline" onClick={() => navigate(`${basePath}/${id}/edit`)}>✏️ Edit</button>
                    <button className="btn btn-danger" onClick={() => setDeleteModal(true)}>🗑 Delete</button>
                </div>
            </div>

            {/* ── IDENTITY ───────────────────────────────────────────────────── */}
            <Section title="Identity" accent="#003b67">
                <Row
                    label="Student Photo"
                    value={
                        studentPhoto ? (
                            <img
                                src={studentPhoto}
                                alt={s.fullName}
                                style={{ width: 150, height: 150, objectFit: "cover", borderRadius: 75, border: "1px solid var(--border)" }}
                            />
                        ) : (
                            initials
                        )
                    }
                />
                <Row label="First Name" value={val(s.firstName)} />
                <Row label="Middle Name" value={val(s.middleName)} />
                <Row label="Last Name" value={val(s.lastName)} />
                <Row label="Full Name" value={val(s.fullName)} />
                <Row label="Gender" value={val(s.gender)} />
                <Row label="Blood Group" value={val(s.bloodGroup)} />
                <Row label="Date of Birth" value={s.dob || "—"} />
                <Row label="DOB in Words" value={val(s.dobInWords)} />
                <Row label="Marital Status" value={val(s.maritalStatus)} />
                <Row label="Aadhaar No." value={val(s.aadharNo)} />
                <Row label="APAAR ID" value={val(s.aaparId)} />
                <Row label="Nationality" value={val(s.nationality)} />
                <Row label="Mother Tongue" value={val(s.motherTongue)} />
                <Row label="Religion" value={val(s.religion)} />
                <Row label="Caste" value={val(s.caste)} />
                <Row label="Sub Caste" value={val(s.subCaste)} />
                <Row label="Institute ID" value={val(s.institueId)} />
            </Section>

            {/* ── ACADEMIC ───────────────────────────────────────────────────── */}
            <Section title="Academic & Admission" accent="#0891b2">
                <Row label="Course" value={val(getCourseName(s.course))} />
                <Row label="Course Year" value={val(s.yearNumber ? `${s.yearNumber}${s.yearNumber === 1 ? "st" : s.yearNumber === 2 ? "nd" : s.yearNumber === 3 ? "rd" : "th"} Year` : "")} />
                <Row label="Enrollment No." value={val(s.enrollmentNo)} />
                <Row label="Roll No." value={val(s.rollNo)} />
                <Row label="General Reg. No." value={val(s.generalRegNo)} />
                <Row label="Admission Type" value={val(s.admissionType)} />
                <Row label="Admission Category" value={val(s.admissionCategory)} />
                <Row label="Student Type" value={val(s.studentType)} />
                <Row label="Institute Admission Date" value={val(s.instituteAdmissionDate)} />
                <Row label="Current Admission Date" value={val(s.currentAdmissionDate)} />
                <Row label="Previous Passing Year" value={val(s.previousPassingYear)} />
                <Row label="Division Name" value={val(s.divisionName)} />
                <Row label="Fee Structure Name" value={val(s.feeStructureName)} />
                <Row label="First Year Stream" value={val(s.firstYearStream)} />
                <Row label="Previous School" value={val(s.previousSchoolName)} wide />
            </Section>

            {/* ── FEES ───────────────────────────────────────────────────────── */}
            <Section title="Fees & Scholarship" accent="#d97706">
                <Row label="Fees Decided" value={inr(s.feesDecided)} />
                <Row label="Scholarship Balance" value={inr(s.scholarshipAmountBalance)} />
                {s.feesDecided > 0 && (
                    <div style={{ gridColumn: "1 / -1", marginTop: "0.5rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                            <span>Scholarship coverage</span>
                            <span>{Math.round((s.scholarshipAmountBalance / s.feesDecided) * 100)}%</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 99, background: "var(--border)", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${Math.min(100, Math.round((s.scholarshipAmountBalance / s.feesDecided) * 100))}%`, background: "linear-gradient(90deg, #16a34a, #22d3ee)", borderRadius: 99 }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                            <span>Balance payable: {inr(Math.max(0, s.feesDecided - s.scholarshipAmountBalance))}</span>
                            <span>Total: {inr(s.feesDecided)}</span>
                        </div>
                        <div style={{ marginTop: "0.85rem", display: "flex", gap: "0.5rem" }}>
                            <button
                                type="button"
                                onClick={() => navigate(feeStatementPath)}
                                className="btn btn-primary"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.45rem",
                                    fontSize: 13,
                                    fontWeight: 600,
                                }}
                            >
                                <FiCreditCard size={15} /> View Fee Statement
                            </button>
                        </div>
                    </div>

                )}
            </Section>

            {/* ── SCHOLARSHIPS ───────────────────────────────────────────────── */}
            <div style={{ marginBottom: "1rem" }}>
                <StudentScholarships studentId={id} />
            </div>

            {/* ── FAMILY ─────────────────────────────────────────────────────── */}
            <Section title="Family Details" accent="#7c3aed">
                <Row label="Father's Name" value={val(s.fatherName)} />
                <Row label="Father's Mobile" value={val(s.fatherMobile)} />
                <Row label="Father's DOB" value={val(s.fatherDob)} />
                <Row label="Father's Education" value={val(s.fatherEduction)} />
                <Row label="Father's Occupation" value={val(s.parentOccupation)} />
                <Row label="Father's Income" value={inr(s.parentIncome)} />
                <Row label="Mother's Name" value={val(s.motherName)} />
                <Row label="Mother's Mobile" value={val(s.motherMobile)} />
                <Row label="Mother's DOB" value={val(s.motherDob)} />
                <Row label="Mother's Education" value={val(s.motherEducation)} />
                <Row label="Mother's Occupation" value={val(s.motherOccupation)} />
                <Row label="Mother's Income" value={inr(s.motherIncome)} />
                <Row label="Telephone No." value={val(s.telephoneNo)} />
                <Row label="Parent Email" value={val(s.parentEmailAddress)} />
                <Row label="Parent Address" value={val(s.parentAddress)} wide />
                <Row label="BPL Card Holder?" value={bool(s.isBplCardHolder)} />
                <Row label="BPL Card No." value={val(s.bplCardNo)} />
                <Row label="Ration Card No." value={val(s.rationCardNo)} />
            </Section>

            {/* ── BIRTH ──────────────────────────────────────────────────────── */}
            <Section title="Birth Details" accent="#dc2626">
                <Row label="Place of Birth" value={val(s.placeOfBirth)} />
                <Row label="Birth District" value={val(s.birthDistrict)} />
                <Row label="Birth Taluka" value={val(s.birthTaluka)} />
                <Row label="Birth State" value={val(s.birthState)} />
                <Row label="Birth Country" value={val(s.birthCountry)} />
                <Row label="Urban / Rural" value={val(s.birthUrbanRural)} />
            </Section>

            {/* ── CONTACT & ADDRESS ──────────────────────────────────────────── */}
            <Section title="Contact & Address" accent="#db2777">
                <Row label="Mobile" value={val(s.mobile)} />
                <Row label="Email" value={val(s.email)} />
                <Row label="Address" value={val(s.address)} wide />
                <Row label="City" value={val(s.city)} />
                <Row label="Taluka" value={val(s.taluka)} />
                <Row label="District" value={val(s.district)} />
                <Row label="State" value={val(s.state)} />
                <Row label="Pincode" value={val(s.pincode)} />
                <Row label="Country" value={val(s.country)} />
                <Row label="Urban / Rural" value={val(s.addressUrbanRural)} />
            </Section>

            {/* ── BANK ───────────────────────────────────────────────────────── */}
            <Section title="Bank Details" accent="#0f766e">
                <Row label="Bank Account No." value={val(s.bankAccountNo)} />
                <Row label="Bank Name" value={val(s.bankName)} />
                <Row label="Branch Name" value={val(s.branchName)} />
                <Row label="IFSC Code" value={val(s.ifscCode)} />
            </Section>

            {/* ── META ───────────────────────────────────────────────────────── */}
            <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right", marginBottom: "1rem" }}>
                Added: {new Date(s.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                &nbsp;·&nbsp;
                Updated: {new Date(s.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </div>

            {/* ── Bottom actions ─────────────────────────────────────────────── */}
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginBottom: "2rem" }}>
                <button className="btn btn-outline" onClick={() => navigate(basePath)}>← Back to List</button>
                <button className="btn btn-outline" onClick={() => navigate(`${basePath}/${id}/edit`)}>✏️ Edit Student</button>
                <button className="btn btn-danger" onClick={() => setDeleteModal(true)}>🗑 Delete Student</button>
            </div>

            {deleteModal && (
                <ConfirmModal
                    title="Delete Student"
                    message={`Delete "${s.fullName}"? Blocked if certificates exist.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteModal(false)}
                    loading={deleting}
                />
            )}
        </div>
    );
}